import { useCallback, useMemo } from "react";

import { type ApolloError, useQuery } from "@apollo/client";

import { useFeatureGates } from "../../../cof-feature-gates/hooks";
import { CofConfigId } from "../../../gql-generated/types";
import { submissionQuotas } from "../../api/queries";
import {
    type SubmissionQuotasQuery,
    type SubmissionQuotasQueryVariables,
} from "../../api/queries/submissionQuotas.gql-generated";
import { type SubmissionQuota, type SubmissionQuotas } from "../../models/SubmissionQuota";
import { getFreshSubmissionQuota, normalizeSubmissionQuotas } from "../../utils/submissionQuota";

const POLL_INTERVAL_MILLISECONDS = 60_000;
const unavailableSubmissionQuotas = (): SubmissionQuotas => ({ regular: null, lensPlus: null });

const getSubmissionQuotas = (
    data: SubmissionQuotasQuery["submissionQuotas"] | undefined,
    now = Date.now(),
): SubmissionQuotas => {
    const quotas = normalizeSubmissionQuotas(data);

    return {
        regular: getFreshSubmissionQuota(quotas.regular, now),
        lensPlus: getFreshSubmissionQuota(quotas.lensPlus, now),
    };
};

export interface SubmissionQuotasResult {
    regular: SubmissionQuota | null;
    lensPlus: SubmissionQuota | null;
    loading: boolean;
    error: ApolloError | undefined;
    refetch: () => Promise<SubmissionQuotas>;
}

export const useSubmissionQuotas = (): SubmissionQuotasResult => {
    const { configs, loading: featureGatesLoading, error: featureGatesError } = useFeatureGates();
    const enabled = configs[CofConfigId.MyLensesSubmissionQuotas].enabled;
    const {
        data,
        loading: quotaLoading,
        error: quotaError,
        refetch: refetchQuery,
    } = useQuery<SubmissionQuotasQuery, SubmissionQuotasQueryVariables>(submissionQuotas, {
        skip: featureGatesLoading || !enabled,
        fetchPolicy: "cache-and-network",
        pollInterval: POLL_INTERVAL_MILLISECONDS,
    });

    const quotas = useMemo(
        () => (enabled && !quotaError ? getSubmissionQuotas(data?.submissionQuotas) : unavailableSubmissionQuotas()),
        [data?.submissionQuotas, enabled, quotaError],
    );
    const refetch = useCallback(async (): Promise<SubmissionQuotas> => {
        if (!enabled) {
            return unavailableSubmissionQuotas();
        }

        try {
            const result = await refetchQuery();
            return getSubmissionQuotas(result.data.submissionQuotas);
        } catch {
            return unavailableSubmissionQuotas();
        }
    }, [enabled, refetchQuery]);

    return {
        ...quotas,
        loading: featureGatesLoading || (enabled && quotaLoading),
        error: featureGatesError ?? quotaError,
        refetch,
    };
};
